import React, { useState, useEffect, useRef } from 'react';
import api from '../../utils/api';
import { useAuth } from '../../context/AuthContext';
import { useToast } from '../../context/ToastContext';
import { useConfirm } from '../../context/ConfirmContext';
import Pagination from '../common/Pagination';
import Card from '../common/Card';
import Button from '../common/Button';
import PageHeader from '../common/PageHeader';
import EmptyState from '../common/EmptyState';
import { LoadingState } from '../common/Spinner';
import { Table, THead, Th, Tr, Td } from '../common/Table';
import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';

const LiterStatementPage = ({ navigationParams, onNavigationComplete, onViewChange }) => {
  const { user, hasPermission, shopSettings } = useAuth();
  const toast = useToast();
  const confirm = useConfirm();
  const [view, setView] = useState('list'); // 'list' or 'register'
  const [statements, setStatements] = useState([]);
  const [loading, setLoading] = useState(false);
  const [currentStatementId, setCurrentStatementId] = useState(null);

  // ── Shop settings: active categories & order ─────────────────────────
  // ALL_CATEGORIES is the full ordered default used when no shop_settings exist
  const ALL_CAT_KEYS = ['Brandy', 'Whisky', 'Rum', 'GinVodka', 'LAB', 'Wine', 'Beer'];
  const ALL_CAT_LABELS = { Brandy: 'Brandy', Whisky: 'Whisky', Rum: 'Rum', GinVodka: 'Gin/Vodka', LAB: 'LAB', Wine: 'Wine', Beer: 'Beer' };

  // Determine which data categories to display, in exact order
  let orderedKeysToDisplay;
  if (shopSettings && shopSettings.categoryOrder && shopSettings.categories) {
    orderedKeysToDisplay = shopSettings.categoryOrder.filter(k => shopSettings.categories.includes(k));
  } else {
    // Default: all categories, with Total at the end
    orderedKeysToDisplay = [...ALL_CAT_KEYS, 'Total'];
  }

  // Categories that feed into the Total column
  let totalCategories = ['Brandy', 'Whisky', 'Rum', 'GinVodka', 'LAB'];
  if (shopSettings && shopSettings.categoryOrder && shopSettings.categories) {
    const totalIndex = orderedKeysToDisplay.indexOf('Total');
    if (totalIndex !== -1) {
      // Sum only categories that come before 'Total'
      totalCategories = orderedKeysToDisplay.slice(0, totalIndex);
    } else {
      totalCategories = orderedKeysToDisplay;
    }
  }

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 5;
  const [modalOpen, setModalOpen] = useState(false);
  const [modalData, setModalData] = useState({ row: '', brand: '', target: '' });
  const [calculationRows, setCalculationRows] = useState([{ bottles: '', ml: '', liter: 0 }]);
  
  const [formData, setFormData] = useState({
    range: '',
    shop_name: '',
    month: '',
    code: '',
    categories: Object.fromEntries(
      ALL_CAT_KEYS.map(k => [k, { opening: '', purchase: '', closing: '' }])
    )
  });

  // Permit Invoice Details state
  const [invoices, setInvoices] = useState([]);

  const mainBrands = ['Brandy', 'Whisky', 'Rum', 'GinVodka', 'LAB'];
  const beerBrand = 'Beer';
  const categoryLabels = ALL_CAT_LABELS;
  const sheetRef = useRef(null);
  const [openingBalanceAutoFilled, setOpeningBalanceAutoFilled] = useState(false);
  const rowFieldMap = { Open: 'opening', Purchase: 'purchase', Closing: 'closing' };

  // Renders the input cell with an embedded calculator icon
  const renderEntryCell = (row, category, viewSuffix = '') => {
    const inputId = `input-${row}-${category}${viewSuffix}`;
    const isOpenAutoReadonly = row === 'Open' && openingBalanceAutoFilled && !currentStatementId;
    
    return (
      <div className="relative flex items-center group w-full">
        <input
          type="number"
          id={inputId}
          className={`data-input text-xs sm:text-sm w-full pr-8 py-1.5 transition-all focus:ring-2 focus:ring-primary/50 focus:border-primary border-gray-200 dark:border-slate-600 rounded-md shadow-inner bg-white dark:bg-slate-700 dark:text-white ${isOpenAutoReadonly ? '!bg-blue-50 dark:!bg-blue-900/20 !cursor-not-allowed text-blue-800 dark:text-blue-300' : ''}`}
          step="0.001"
          min="0"
          value={formData.categories[category]?.[rowFieldMap[row]] || ''}
          onChange={(e) => handleInputChange(category, rowFieldMap[row], e.target.value)}
          onWheel={(e) => e.target.blur()}
          onKeyDown={(e) => ['ArrowUp', 'ArrowDown'].includes(e.key) && e.preventDefault()}
          placeholder="0"
          readOnly={isOpenAutoReadonly}
          title={isOpenAutoReadonly ? 'Auto-filled from previous month (read-only)' : ''}
        />
        {!isOpenAutoReadonly && (
          <button
            type="button"
            onClick={() => openCalculationModal(row, category, inputId)}
            className="absolute right-1 text-gray-400 hover:text-primary transition-colors p-1 rounded hover:bg-gray-100 dark:hover:bg-slate-600 flex items-center justify-center opacity-70 group-hover:opacity-100"
            title={`Calculate ${row} for ${category}`}
            aria-label={`Calculate ${row} for ${category}`}
            tabIndex="-1"
          >
            <i className="fas fa-calculator text-[10px] sm:text-xs" aria-hidden="true"></i>
          </button>
        )}
      </div>
    );
  };

  // Load statements list
  useEffect(() => {
    if (view === 'list') {
      loadStatements();
      setCurrentPage(1); // Reset to first page when switching to list view
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [view]);

  // Handle navigation parameters from dashboard
  useEffect(() => {
    if (navigationParams && view === 'list') {
      if (navigationParams.statementId) {
        // Find and highlight the statement
        setTimeout(() => {
          const statementElement = document.getElementById(`statement-${navigationParams.statementId}`);
          if (statementElement) {
            statementElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
            statementElement.classList.add('ring-2', 'ring-primary', 'ring-offset-2');
            setTimeout(() => {
              statementElement.classList.remove('ring-2', 'ring-primary', 'ring-offset-2');
            }, 3000);
          }
          // Clear navigation params after processing
          if (onNavigationComplete) {
            setTimeout(() => onNavigationComplete(), 1000);
          }
        }, 500);
      } else if (onNavigationComplete) {
        // Clear params even if no statementId
        setTimeout(() => onNavigationComplete(), 500);
      }
    }
  }, [navigationParams, view, onNavigationComplete]);

  // Calculate totals whenever form data changes
  useEffect(() => {
    calculateReport();
  }, [formData]);

  // Notify parent when entering/exiting entry mode
  useEffect(() => {
    if (onViewChange) {
      onViewChange(view === 'register');
    }
  }, [view, onViewChange]);

  // Auto-fill shop name and code from user profile when creating new statement
  useEffect(() => {
    if (view === 'register' && !currentStatementId && user && (user.shop_name || user.shop_code) && (!formData.shop_name || !formData.code)) {
      setFormData(prev => ({
        ...prev,
        shop_name: user.shop_name || '',
        code: user.shop_code || ''
      }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [view, currentStatementId, user]);

  // Auto-fill opening balance from previous month when month is selected
  useEffect(() => {
    const fetchPreviousClosingBalance = async () => {
      // Only fetch if creating new statement (not editing) and month is selected
      if (view === 'register' && !currentStatementId && formData.month) {
        try {
          const [year, month] = formData.month.split('-');
          
          const response = await api.get(
            `/liter-statements/previous-closing/${month}/${year}`
          );

          if (response.data.closingBalances) {
            const closingBalances = response.data.closingBalances;
            
            // Update opening balance for each category
            const updatedCategories = { ...formData.categories };
            Object.keys(closingBalances).forEach(category => {
              if (updatedCategories[category]) {
                updatedCategories[category] = {
                  ...updatedCategories[category],
                  opening: closingBalances[category] > 0 ? closingBalances[category].toFixed(3) : ''
                };
              }
            });

            setFormData(prev => ({
              ...prev,
              categories: updatedCategories
            }));

            setOpeningBalanceAutoFilled(true);
          } else {
            setOpeningBalanceAutoFilled(false);
          }
        } catch (error) {
          console.error('Error fetching previous closing balance:', error);
          setOpeningBalanceAutoFilled(false);
        }
      } else if (currentStatementId) {
        // When editing, opening balance is not auto-filled
        setOpeningBalanceAutoFilled(false);
      }
    };

    fetchPreviousClosingBalance();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData.month, view, currentStatementId]);

  const loadStatements = async () => {
    try {
      setLoading(true);
      const response = await api.get('/liter-statements');
      setStatements(response.data.statements || []);
    } catch (error) {
      console.error('Error loading statements:', error);
      setStatements([]);
      if (error.response) {
        if (error.response.status === 401) {
          toast.error('Your session has expired. Please log in again.');
        } else if (error.response.status === 403) {
          toast.error('You do not have permission to view liter statements.');
        } else if (error.response.data && error.response.data.error) {
          toast.error(`Error: ${error.response.data.error}`);
        } else {
          toast.error('Failed to load liter statements. Please try again.');
        }
      } else if (error.request) {
        toast.error('Network error: Could not connect to server. Please check your connection and try again.');
      } else {
        toast.error('Failed to load liter statements. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  const loadStatement = async (id) => {
    try {
      setLoading(true);
      const response = await api.get(`/liter-statements/${id}`);
      const { statement, items, invoices } = response.data;
      
      // Parse month and year
      const monthYear = `${statement.year}-${String(statement.month).padStart(2, '0')}`;
      
      // Initialize categories
      const categories = {
        Brandy: { opening: '', purchase: '', closing: '' },
        Whisky: { opening: '', purchase: '', closing: '' },
        Rum: { opening: '', purchase: '', closing: '' },
        GinVodka: { opening: '', purchase: '', closing: '' },
        LAB: { opening: '', purchase: '', closing: '' },
        Wine: { opening: '', purchase: '', closing: '' },
        Beer: { opening: '', purchase: '', closing: '' }
      };

      // Map items to categories
      items.forEach(item => {
        const category = item.category;
        if (categories[category]) {
          categories[category] = {
            opening: item.opening || '',
            purchase: item.purchase || '',
            closing: item.closing || ''
          };
        }
      });

      setFormData({
        range: statement.range || '',
        shop_name: statement.shop_name || '',
        month: monthYear,
        code: statement.code || '',
        categories
      });

      setCurrentStatementId(statement.id);
      setInvoices(invoices || []);
      setView('register');
    } catch (error) {
      console.error('Error loading statement:', error);
      toast.error('Failed to load statement');
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    let nextMonthYear = '';
    if (statements && statements.length > 0) {
      const latestStatement = statements[0];
      let nextMonth = parseInt(latestStatement.month) + 1;
      let nextYear = parseInt(latestStatement.year);
      if (nextMonth > 12) {
        nextMonth = 1;
        nextYear++;
      }
      nextMonthYear = `${nextYear}-${String(nextMonth).padStart(2, '0')}`;
    }

    setFormData({
      range: '',
      shop_name: user?.shop_name || '',
      month: nextMonthYear,
      code: user?.shop_code || '',
      categories: Object.fromEntries(
        ALL_CAT_KEYS.map(k => [k, { opening: '', purchase: '', closing: '' }])
      )
    });
    setInvoices([]);
    setCurrentStatementId(null);
    setOpeningBalanceAutoFilled(false);
  };

  const handleInputChange = (category, field, value) => {
    setFormData(prev => ({
      ...prev,
      categories: {
        ...prev.categories,
        [category]: {
          ...prev.categories[category],
          [field]: value
        }
      }
    }));
  };

  const getValue = (category, field) => {
    return parseFloat(formData.categories[category]?.[field] || 0);
  };

  const calculateReport = () => {
    // This is handled by the display calculations in render
  };

  const getTotal = (field) => {
    let total = 0;
    totalCategories.forEach(brand => {
      total += getValue(brand, field);
    });
    return total;
  };

  const getTotalStock = (category) => {
    return getValue(category, 'opening') + getValue(category, 'purchase');
  };

  const getSales = (category) => {
    return getTotalStock(category) - getValue(category, 'closing');
  };

  const getBeerTotalStock = () => {
    return getValue(beerBrand, 'opening') + getValue(beerBrand, 'purchase');
  };

  const getBeerSales = () => {
    return getBeerTotalStock() - getValue(beerBrand, 'closing');
  };

  const saveStatement = async () => {
    try {
      if (!formData.month) {
        toast.error('Please select a month');
        return;
      }

      const showInvoiceDetails = hasPermission('liter_statement', 'permit_invoice_details');
      const [year, month] = formData.month.split('-');
      
      // Prepare items array — always save all 7 categories to DB regardless of visibility
      const items = ALL_CAT_KEYS.map(cat => ({
        category: cat,
        opening: getValue(cat, 'opening'),
        purchase: getValue(cat, 'purchase'),
        closing: getValue(cat, 'closing'),
        sales: getSales(cat)
      }));

      const payload = {
        month: parseInt(month),
        year: parseInt(year),
        range: formData.range,
        shop_name: formData.shop_name,
        code: formData.code,
        items
      };

      // Include invoices in payload if authorized
      if (showInvoiceDetails) {
        payload.invoices = invoices;
      }

      setLoading(true);
      
      if (currentStatementId) {
        await api.put(`/liter-statements/${currentStatementId}`, payload);
        toast.success('Statement updated successfully!');
      } else {
        await api.post('/liter-statements', payload);
        toast.success('Statement saved successfully!');
      }

      setView('list');
      loadStatements();
    } catch (error) {
      console.error('Error saving statement:', error);
      toast.error(error.response?.data?.error || 'Failed to save statement');
    } finally {
      setLoading(false);
    }
  };

  const deleteStatement = async (id) => {
    const confirmed = await confirm({
      title: 'Delete this statement?',
      message: 'This action cannot be undone.',
      confirmLabel: 'Delete',
      variant: 'danger'
    });
    if (!confirmed) return;

    try {
      await api.delete(`/liter-statements/${id}`);
      loadStatements();
      toast.success('Statement deleted successfully!');
    } catch (error) {
      console.error('Error deleting statement:', error);
      toast.error('Failed to delete statement');
    }
  };

  const downloadPDF = async (statementId = null) => {
    try {
      // Local variables to hold data for PDF generation
      let pdfFormData = { ...formData };
      let pdfInvoices = [...invoices];
      
      // Calculate helpers for local data
      const getPdfValue = (category, field) => parseFloat(pdfFormData.categories[category]?.[field] || 0);
      const getPdfTotal = (field) => Array.from(totalCategories).reduce((sum, brand) => sum + getPdfValue(brand, field), 0);
      const getPdfTotalStock = (category) => getPdfValue(category, 'opening') + getPdfValue(category, 'purchase');
      const getPdfClosingBalance = (category) => getPdfTotalStock(category) - getPdfValue(category, 'sales');

      if (statementId) {
        // Fetch fresh data explicitly for PDF without relying on React state
        const response = await api.get(`/liter-statements/${statementId}`);
        const { statement, items, invoices: fetchedInvoices } = response.data;
        
        const monthYear = `${statement.year}-${String(statement.month).padStart(2, '0')}`;
        
        const categories = {
          Brandy: { opening: '', purchase: '', closing: '' },
          Whisky: { opening: '', purchase: '', closing: '' },
          Rum: { opening: '', purchase: '', closing: '' },
          GinVodka: { opening: '', purchase: '', closing: '' },
          LAB: { opening: '', purchase: '', closing: '' },
          Wine: { opening: '', purchase: '', closing: '' },
          Beer: { opening: '', purchase: '', closing: '' }
        };

        items.forEach(item => {
          if (categories[item.category]) {
            categories[item.category] = {
              opening: item.opening || '',
              purchase: item.purchase || '',
              closing: item.closing || ''
            };
          }
        });

        pdfFormData = {
          range: statement.range || '',
          shop_name: statement.shop_name || '',
          month: monthYear,
          code: statement.code || '',
          categories
        };
        
        pdfInvoices = fetchedInvoices || [];
      }

      const doc = new jsPDF({ orientation: 'landscape', unit: 'mm', format: 'a4' });
      const pageWidth = doc.internal.pageSize.getWidth();
      const marginX = 14;

      // Colors from the theme
      const BRAND = { primary: [67, 97, 238], ink: [15, 43, 87], muted: [100, 116, 139], border: [226, 232, 240], panel: [248, 250, 252] };

      // Title & Month
      let y = 15;
      doc.setFontSize(14);
      doc.setTextColor(...BRAND.ink);
      doc.setFont(undefined, 'bold');
      doc.text('MONTHLY LITER STATEMENT', pageWidth / 2, y, { align: 'center' });
      
      y += 6;
      let displayMonth = pdfFormData.month || '-';
      if (pdfFormData.month) {
        try {
          const [year, monthNum] = pdfFormData.month.split('-');
          const date = new Date(year, parseInt(monthNum) - 1);
          displayMonth = `${date.toLocaleString('default', { month: 'long' })} - ${year}`;
        } catch (e) {
          // ignore
        }
      }
      doc.setFontSize(10);
      doc.setFont(undefined, 'normal');
      doc.text(`MONTH: ${displayMonth}`, pageWidth / 2, y, { align: 'center' });

      // Shop Details
      y += 10;
      doc.setFontSize(10);
      doc.text(`Range: ${pdfFormData.range || '-'}`, marginX, y);
      doc.text(`Shop Code: ${pdfFormData.code || '-'}`, pageWidth - marginX, y, { align: 'right' });
      y += 6;
      doc.text(`Shop Name: ${pdfFormData.shop_name || '-'}`, marginX, y);
      
      y += 8;

      // Table columns mapping
      const tableColumns = ['Category', ...orderedKeysToDisplay.map(k => k === 'Total' ? 'TOTAL' : (ALL_CAT_LABELS[k] || k))];
      
      // Table rows
      const tableRows = [
        ['OPENING BALANCE', ...orderedKeysToDisplay.map(k => k === 'Total' ? getPdfTotal('opening').toFixed(3) : getPdfValue(k, 'opening').toFixed(3))],
        ['PURCHASE', ...orderedKeysToDisplay.map(k => k === 'Total' ? getPdfTotal('purchase').toFixed(3) : getPdfValue(k, 'purchase').toFixed(3))],
        ['TOTAL STOCK', ...orderedKeysToDisplay.map(k => k === 'Total' ? (getPdfTotal('opening') + getPdfTotal('purchase')).toFixed(3) : getPdfTotalStock(k).toFixed(3))],
        ['SALES', ...orderedKeysToDisplay.map(k => {
          if (k === 'Total') {
            return (getPdfTotal('opening') + getPdfTotal('purchase') - getPdfTotal('closing')).toFixed(3);
          }
          return (getPdfTotalStock(k) - getPdfValue(k, 'closing')).toFixed(3);
        })],
        ['CLOSING BALANCE', ...orderedKeysToDisplay.map(k => k === 'Total' ? getPdfTotal('closing').toFixed(3) : getPdfValue(k, 'closing').toFixed(3))]
      ];

      autoTable(doc, {
        startY: y,
        head: [tableColumns],
        body: tableRows,
        styles: { fontSize: 9, cellPadding: 3, halign: 'center', valign: 'middle' },
        columnStyles: { 0: { halign: 'left', fontStyle: 'bold' } },
        headStyles: { fillColor: BRAND.primary, textColor: 255, fontStyle: 'bold' },
        alternateRowStyles: { fillColor: BRAND.panel },
        margin: { left: marginX, right: marginX },
        theme: 'grid',
        didParseCell: function(data) {
           const totalColIndex = tableColumns.indexOf('TOTAL');
           // Highlight total column
           if (data.column.index === totalColIndex) {
              data.cell.styles.fontStyle = 'bold';
              if (data.section === 'body') {
                data.cell.styles.textColor = BRAND.primary;
              }
           }
           // Highlight Total Stock row
           if (data.row.index === 2 && data.section === 'body') {
              data.cell.styles.fillColor = [230, 240, 255]; // Light blue
           }
        }
      });

      y = doc.lastAutoTable.finalY + 6;

      // Add Permit Invoices if they exist
      if (hasPermission('liter_statement', 'permit_invoice_details') && pdfInvoices && pdfInvoices.length > 0) {
        doc.setFontSize(12);
        doc.setFont(undefined, 'bold');
        doc.setTextColor(...BRAND.ink);
        doc.text('Permit Invoice Details', marginX, y);
        y += 5;

        const invoiceColumns = ['S.No', 'Date', 'Invoice No', 'IML', 'BEER', 'Wine', 'LAB', 'Total'];
        const invoiceRows = pdfInvoices.map((inv, idx) => {
          let displayDate = inv.date || '-';
          if (inv.date) {
            try {
              const [year, month, day] = inv.date.split('T')[0].split('-');
              if (year && month && day) {
                displayDate = `${day}-${month}-${year}`;
              }
            } catch(e) {}
          }
          return [
            idx + 1,
            displayDate,
            inv.invoice_no || '-',
            parseFloat(inv.iml || 0).toFixed(3),
            parseFloat(inv.beer || 0).toFixed(3),
            parseFloat(inv.wine || 0).toFixed(3),
            parseFloat(inv.lab || 0).toFixed(3),
            parseFloat(inv.total || 0).toFixed(3)
          ];
        });

        // Add summary footer for invoices
        const getPdfInvoiceTotals = () => {
          const totals = { iml: 0, beer: 0, wine: 0, lab: 0, total: 0 };
          pdfInvoices.forEach(inv => {
            totals.iml += parseFloat(inv.iml) || 0;
            totals.beer += parseFloat(inv.beer) || 0;
            totals.wine += parseFloat(inv.wine) || 0;
            totals.lab += parseFloat(inv.lab) || 0;
            totals.total += parseFloat(inv.total) || 0;
          });
          return totals;
        };
        const totals = getPdfInvoiceTotals();
        invoiceRows.push([
          { content: 'Summary Total:', colSpan: 3, styles: { halign: 'right', fontStyle: 'bold' } },
          totals.iml.toFixed(3),
          totals.beer.toFixed(3),
          totals.wine.toFixed(3),
          totals.lab.toFixed(3),
          totals.total.toFixed(3)
        ]);

        autoTable(doc, {
          startY: y,
          head: [invoiceColumns],
          body: invoiceRows,
          styles: { fontSize: 8, cellPadding: 2, halign: 'center' },
          columnStyles: { 
            0: { halign: 'center' },
            1: { halign: 'left' },
            2: { halign: 'left' },
            3: { halign: 'right' },
            4: { halign: 'right' },
            5: { halign: 'right' },
            6: { halign: 'right' },
            7: { halign: 'right', fontStyle: 'bold', textColor: BRAND.primary }
          },
          headStyles: { fillColor: [100, 116, 139], textColor: 255 },
          margin: { left: marginX, right: marginX },
          theme: 'grid'
        });
      }

      // Add footer to all pages
      const pageCount = doc.internal.getNumberOfPages();
      const pageHeight = doc.internal.pageSize.getHeight();
      for (let i = 1; i <= pageCount; i++) {
        doc.setPage(i);
        doc.setFontSize(7.5);
        doc.setTextColor(...BRAND.muted);
        doc.text(`Page ${i} of ${pageCount}  ·  BizTracker`, pageWidth / 2, pageHeight - 8, { align: 'center' });
      }

      // Format month for filename
      const monthInput = pdfFormData.month;
      let filename = 'Monthly_Stock_Register';
      if (monthInput) {
        try {
          const [year, monthNum] = monthInput.split('-');
          const date = new Date(year, monthNum - 1);
          filename = `${date.toLocaleString('default', { month: 'long', year: 'numeric' }).replace(/[^a-z0-9]/gi, '_')}_Stock_Register`;
        } catch (e) {
          filename = monthInput.replace(/[^a-z0-9]/gi, '_') + '_Stock_Register';
        }
      }

      doc.save(`${filename}.pdf`);
    } catch (error) {
      console.error('Error generating PDF:', error);
      toast.error('Failed to generate PDF');
    }
  };

  // Permit Invoice Details helper and CRUD functions
  const MAX_INVOICE_ROWS = 20;

  const handleAddInvoiceRow = () => {
    if (invoices.length >= MAX_INVOICE_ROWS) {
      toast.warning(`Maximum ${MAX_INVOICE_ROWS} invoice rows allowed.`);
      return;
    }
    const newRow = {
      id: `temp_${Date.now()}`,
      date: '',
      invoice_no: '',
      iml: '',
      beer: '',
      wine: '',
      lab: '',
      total: 0
    };
    setInvoices(prev => [...prev, newRow]);
  };

  const handleDeleteInvoiceRow = async (id) => {
    const confirmed = await confirm({
      title: 'Delete invoice record?',
      message: 'This row will be removed upon saving the report.',
      confirmLabel: 'Delete',
      variant: 'danger'
    });
    if (!confirmed) return;
    setInvoices(prev => prev.filter(inv => inv.id !== id));
  };

  const handleInvoiceFieldChange = (id, field, value) => {
    setInvoices(prev => prev.map(inv => {
      if (inv.id !== id) return inv;
      const updated = { ...inv, [field]: value };
      if (['iml', 'beer', 'wine', 'lab'].includes(field)) {
        const iml = parseFloat(field === 'iml' ? value : updated.iml) || 0;
        const beer = parseFloat(field === 'beer' ? value : updated.beer) || 0;
        const wine = parseFloat(field === 'wine' ? value : updated.wine) || 0;
        const lab = parseFloat(field === 'lab' ? value : updated.lab) || 0;
        updated.total = iml + beer + wine + lab;
      }
      return updated;
    }));
  };

  const getInvoiceTotals = () => {
    const totals = { iml: 0, beer: 0, wine: 0, lab: 0, total: 0 };
    invoices.forEach(inv => {
      totals.iml += parseFloat(inv.iml) || 0;
      totals.beer += parseFloat(inv.beer) || 0;
      totals.wine += parseFloat(inv.wine) || 0;
      totals.lab += parseFloat(inv.lab) || 0;
      totals.total += parseFloat(inv.total) || 0;
    });
    return totals;
  };

  const openCalculationModal = (row, brand, targetId) => {
    setModalData({ row, brand, target: targetId });
    setCalculationRows([{ bottles: '', ml: '', liter: 0 }]);
    setModalOpen(true);
  };

  const closeModal = () => {
    setModalOpen(false);
    setCalculationRows([{ bottles: '', ml: '', liter: 0 }]);
  };

  const addCalculationRow = () => {
    setCalculationRows([...calculationRows, { bottles: '', ml: '', liter: 0 }]);
  };

  const removeCalculationRow = (index) => {
    if (calculationRows.length > 1) {
      setCalculationRows(prev => prev.filter((_, i) => i !== index));
    } else {
      setCalculationRows([{ bottles: '', ml: '', liter: 0 }]);
    }
  };

  const updateCalculationRow = (index, field, value) => {
    const newRows = [...calculationRows];
    newRows[index] = { ...newRows[index], [field]: value };
    
    // Calculate liter
    const bottles = parseFloat(newRows[index].bottles) || 0;
    const ml = parseFloat(newRows[index].ml) || 0;
    newRows[index].liter = (bottles * ml) / 1000;
    
    setCalculationRows(newRows);
  };

  const applyCalculation = () => {
    const total = calculationRows.reduce((sum, row) => sum + (parseFloat(row.liter) || 0), 0);
    const category = modalData.brand;
    const field = rowFieldMap[modalData.row] || 'closing';
    handleInputChange(category, field, total.toFixed(3));
    if (modalData.target) {
      requestAnimationFrame(() => document.getElementById(modalData.target)?.focus());
    }
    closeModal();
  };

  const formatMonth = (month, year) => {
    try {
      const date = new Date(year, month - 1);
      return date.toLocaleString('default', { month: 'short', year: 'numeric' });
    } catch (e) {
      return `${month}/${year}`;
    }
  };

  // Pagination calculations
  const totalPages = Math.ceil(statements.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const paginatedStatements = statements.slice(startIndex, endIndex);

  if (view === 'list') {
    return (
      <div className="max-w-7xl mx-auto px-2 sm:px-3">
        <PageHeader
          icon="fa-file-alt"
          title="My Liter Statement Reports"
          subtitle="Monthly stock register summaries by category"
          actions={hasPermission('liter_statement', 'create') && (
            <Button
              variant="success"
              icon="fa-plus-circle"
              onClick={() => {
                resetForm();
                setView('register');
              }}
            >
              Create New Report
            </Button>
          )}
        />

        <Card padding="none" className="overflow-hidden">
          {loading ? (
            <LoadingState label="Loading reports..." />
          ) : statements.length === 0 ? (
            <EmptyState
              icon="fa-folder-open"
              title="No Reports Found"
              description="You haven't created any stock reports yet."
              action={hasPermission('liter_statement', 'create') && (
                <Button
                  icon="fa-plus-circle"
                  onClick={() => {
                    resetForm();
                    setView('register');
                  }}
                >
                  Create Your First Report
                </Button>
              )}
            />
          ) : (
            <>
              {/* Desktop Table View */}
              <div className="hidden md:block p-4 sm:p-6">
                <Table>
                  <THead>
                    <tr>
                      <Th>Sl No</Th>
                      <Th>Month</Th>
                      <Th>Opening Balance</Th>
                      <Th>Purchase</Th>
                      <Th>Total</Th>
                      <Th>Sales</Th>
                      <Th>Closing Balance</Th>
                      <Th>Beer Total</Th>
                      <Th>Action</Th>
                    </tr>
                  </THead>
                  <tbody>
                    {paginatedStatements.map((stmt, index) => {
                      const totals = stmt.totals || {
                        opening: '0.000',
                        purchase: '0.000',
                        total: '0.000',
                        sales: '0.000',
                        closing: '0.000',
                        beer: '0.000'
                      };
                      const serialNumber = startIndex + index + 1;

                      return (
                        <Tr key={stmt.id} id={`statement-${stmt.id}`}>
                          <Td>{serialNumber}</Td>
                          <Td className="font-medium">{formatMonth(stmt.month, stmt.year)}</Td>
                          <Td>{totals.opening}</Td>
                          <Td>{totals.purchase}</Td>
                          <Td className="font-bold text-gray-800 dark:text-slate-100">{totals.total}</Td>
                          <Td>{totals.sales}</Td>
                          <Td className="font-bold text-gray-800 dark:text-slate-100">{totals.closing}</Td>
                          <Td className="font-bold text-gray-800 dark:text-slate-100">{totals.beer}</Td>
                          <Td>
                            <div className="flex flex-wrap gap-1.5">
                              {hasPermission('liter_statement', 'edit') && (
                                <Button size="sm" variant="info" icon="fa-edit" onClick={() => loadStatement(stmt.id)}>Edit</Button>
                              )}
                              {hasPermission('liter_statement', 'delete') && (
                                <Button size="sm" variant="danger" icon="fa-trash" onClick={() => deleteStatement(stmt.id)}>Delete</Button>
                              )}
                              {hasPermission('liter_statement', 'view') && (
                                <Button size="sm" variant="purple" icon="fa-download" onClick={() => downloadPDF(stmt.id)}>PDF</Button>
                              )}
                            </div>
                          </Td>
                        </Tr>
                      );
                    })}
                  </tbody>
                </Table>
              </div>

              {/* Mobile Card View */}
              <div className="md:hidden divide-y divide-gray-100">
                {paginatedStatements.map((stmt, index) => {
                  const totals = stmt.totals || {
                    opening: '0.000',
                    purchase: '0.000',
                    total: '0.000',
                    sales: '0.000',
                    closing: '0.000',
                    beer: '0.000'
                  };
                  const serialNumber = startIndex + index + 1;

                  return (
                    <div key={stmt.id} id={`statement-${stmt.id}`} className="p-4 space-y-3">
                      <div>
                        <span className="text-xs text-gray-400 dark:text-slate-500">#{serialNumber}</span>
                        <h3 className="text-base font-bold text-primary-dark">{formatMonth(stmt.month, stmt.year)}</h3>
                      </div>
                      <div className="grid grid-cols-2 gap-2 text-sm">
                        <div className="bg-gray-50 dark:bg-slate-700/50 p-2 rounded">
                          <div className="text-xs text-gray-500 dark:text-slate-400 mb-1">Opening Balance</div>
                          <div className="font-semibold">{totals.opening}</div>
                        </div>
                        <div className="bg-gray-50 dark:bg-slate-700/50 p-2 rounded">
                          <div className="text-xs text-gray-500 dark:text-slate-400 mb-1">Purchase</div>
                          <div className="font-semibold">{totals.purchase}</div>
                        </div>
                        <div className="bg-blue-50 p-2 rounded">
                          <div className="text-xs text-blue-600 mb-1">Total</div>
                          <div className="font-bold text-blue-700">{totals.total}</div>
                        </div>
                        <div className="bg-gray-50 dark:bg-slate-700/50 p-2 rounded">
                          <div className="text-xs text-gray-500 dark:text-slate-400 mb-1">Sales</div>
                          <div className="font-semibold">{totals.sales}</div>
                        </div>
                        <div className="bg-green-50 dark:bg-emerald-500/10 p-2 rounded">
                          <div className="text-xs text-green-600 dark:text-emerald-400 mb-1">Closing Balance</div>
                          <div className="font-bold text-green-700 dark:text-emerald-400">{totals.closing}</div>
                        </div>
                        <div className="bg-purple-50 dark:bg-purple-500/10 p-2 rounded">
                          <div className="text-xs text-purple-600 mb-1">Beer Total</div>
                          <div className="font-bold text-purple-700">{totals.beer}</div>
                        </div>
                      </div>
                      <div className="flex flex-wrap gap-2 pt-2 border-t border-gray-100 dark:border-slate-700/60">
                        {hasPermission('liter_statement', 'edit') && (
                          <Button size="sm" variant="info" icon="fa-edit" onClick={() => loadStatement(stmt.id)} className="flex-1">Edit</Button>
                        )}
                        {hasPermission('liter_statement', 'delete') && (
                          <Button size="sm" variant="danger" icon="fa-trash" onClick={() => deleteStatement(stmt.id)} className="flex-1">Delete</Button>
                        )}
                        {hasPermission('liter_statement', 'view') && (
                          <Button size="sm" variant="purple" icon="fa-download" onClick={() => downloadPDF(stmt.id)} className="flex-1">PDF</Button>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* Pagination Controls */}
              {totalPages > 1 && (
                <div className="mt-1 pt-4 pb-4 sm:pb-6 border-t border-gray-100 dark:border-slate-700/60 flex flex-col items-center gap-2">
                  <Pagination currentPage={currentPage} totalPages={totalPages} onPageChange={setCurrentPage} />
                  <span className="text-xs font-semibold text-gray-500 dark:text-slate-400">Page {currentPage} of {totalPages}</span>
                </div>
              )}
            </>
          )}
        </Card>
      </div>
    );
  }

  return (
    <div className="p-2 sm:p-4 md:p-8 min-h-dvh-screen bg-gradient-to-tr from-indigo-50/70 via-blue-50/50 to-purple-50/60">
      <div className="max-w-7xl mx-auto">
        {/* Control Panel */}
        <div className="bg-gradient-to-r from-primary via-primary-dark to-indigo-600 text-white rounded-xl sm:rounded-3xl p-3 sm:p-6 mb-4 sm:mb-6 shadow-xl shadow-primary/20 relative overflow-hidden sticky top-2 sm:top-4 z-40 backdrop-blur-md bg-opacity-95">
          <div className="absolute top-0 right-0 w-64 h-64 bg-white dark:bg-slate-800/10 rounded-full blur-3xl -mr-32 -mt-32 pointer-events-none"></div>
          
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-3 relative z-10">
            {/* Title */}
            <div className="flex items-center gap-2 sm:gap-3">
              <span className="w-8 h-8 sm:w-10 sm:h-10 rounded-lg sm:rounded-xl bg-white/15 flex items-center justify-center shrink-0">
                <i className="fas fa-chart-bar text-sm sm:text-base"></i>
              </span>
              <h1 className="text-base sm:text-xl md:text-2xl font-bold leading-tight">
                Liter Statement
              </h1>
            </div>
            
            {/* Action Buttons */}
            <div className="flex flex-row items-center justify-between md:justify-end gap-1.5 sm:gap-2 w-full md:w-auto">
              <button
                onClick={() => {
                  setView('list');
                  resetForm();
                }}
                className="flex-1 md:flex-none bg-white/15 hover:bg-white/25 border border-white/20 px-2 sm:px-4 py-2 sm:py-2.5 rounded-lg sm:rounded-xl transition-all duration-300 flex items-center justify-center gap-1.5 font-semibold shadow-sm hover:shadow-md text-xs sm:text-sm whitespace-nowrap"
              >
                <i className="fas fa-arrow-left"></i> <span>Back</span>
              </button>
              
              {hasPermission('liter_statement', 'view') && currentStatementId && (
                <button
                  onClick={() => downloadPDF()}
                  className="flex-1 md:flex-none bg-white/15 hover:bg-white/25 border border-white/20 px-2 sm:px-4 py-2 sm:py-2.5 rounded-lg sm:rounded-xl transition-all duration-300 flex items-center justify-center gap-1.5 font-semibold shadow-sm hover:shadow-md text-xs sm:text-sm whitespace-nowrap"
                >
                  <i className="fas fa-file-pdf"></i> <span>PDF</span>
                </button>
              )}
              
              {(currentStatementId ? hasPermission('liter_statement', 'edit') : hasPermission('liter_statement', 'create')) && (
                <button
                  onClick={saveStatement}
                  disabled={loading}
                  className="flex-[2] md:flex-none bg-emerald-500 hover:bg-emerald-600 text-white border border-emerald-400/50 px-2 sm:px-4 py-2 sm:py-2.5 rounded-lg sm:rounded-xl transition-all duration-300 flex items-center justify-center gap-1.5 font-bold shadow-sm hover:shadow-md text-xs sm:text-sm whitespace-nowrap disabled:opacity-70 disabled:cursor-not-allowed"
                >
                  {loading ? <i className="fas fa-spinner fa-spin"></i> : <i className="fas fa-save"></i>}
                  <span className="sm:hidden">Save</span>
                  <span className="hidden sm:inline">Save Record</span>
                </button>
              )}
            </div>
          </div>
        </div>

        {/* Report Sheet */}
        <div ref={sheetRef} className="bg-white dark:bg-slate-800 rounded-2xl sm:rounded-3xl shadow-xl border border-slate-100 p-3 sm:p-6 md:p-8 lg:p-10">
          {/* Header */}
          <div className="flex flex-col md:flex-row justify-between items-start gap-4 md:gap-4 border-b pb-4 border-gray-200 dark:border-slate-700 mb-4 sm:mb-6">
            <div className="w-full md:w-auto text-xs sm:text-sm text-gray-500 dark:text-slate-400 space-y-2">
              <div className="flex flex-col sm:flex-row items-start sm:items-center gap-1 sm:gap-0 font-semibold">
                <span className="flex items-center sm:w-40 shrink-0">
                  <i className="fas fa-map-marker-alt mr-1"></i>
                  Range / ವಲಯ:
                </span>
                <input
                  type="text"
                  value={formData.range}
                  onChange={(e) => setFormData({ ...formData, range: e.target.value })}
                  maxLength={50}
                  className="header-input ml-0 sm:ml-1 w-full sm:w-auto flex-1 sm:flex-none"
                  placeholder="Enter range"
                />
              </div>
              <div className="flex flex-col sm:flex-row items-start sm:items-center gap-1 sm:gap-0 font-semibold">
                <span className="flex items-center sm:w-40 shrink-0">
                  <i className="fas fa-store mr-1"></i>
                  SHOP NAME / ಸನ್ನದು:
                </span>
                <input
                  type="text"
                  value={formData.shop_name}
                  onChange={(e) => setFormData({ ...formData, shop_name: e.target.value })}
                  maxLength={50}
                  className="header-input ml-0 sm:ml-1 w-full sm:w-auto flex-1 sm:flex-none"
                  placeholder="Enter shop name"
                />
              </div>
            </div>
            <div className="text-center flex-1 w-full md:w-auto">
              <div className="text-center font-extrabold text-lg sm:text-xl md:text-2xl tracking-tight text-primary-dark mb-1">
                MONTHLY LITER STATEMENT
              </div>
              <small className="block font-medium text-xs sm:text-sm text-gray-500 dark:text-slate-400 mt-1 flex flex-col sm:flex-row items-center justify-center gap-1">
                <span className="flex items-center">
                  <i className="fas fa-calendar-alt mr-1"></i>
                  MONTH:
                </span>
                <input
                  type="month"
                  value={formData.month}
                  onChange={(e) => setFormData({ ...formData, month: e.target.value })}
                  className="header-input text-xs sm:text-sm text-center ml-0 sm:ml-1 w-full sm:w-auto min-w-[110px] max-w-[160px] sm:min-w-[140px] sm:max-w-[200px]"
                />
              </small>
            </div>
            <div className="w-full md:w-auto text-right text-xs sm:text-sm text-gray-500 dark:text-slate-400 space-y-2">
              <div className="font-semibold flex flex-col sm:flex-row items-end sm:items-center justify-end gap-1 sm:gap-0">
                <span className="flex items-center justify-end">
                  <i className="fas fa-hashtag mr-1"></i>
                  SHOP CODE:
                </span>
                <input
                  type="text"
                  value={formData.code}
                  onChange={(e) => setFormData({ ...formData, code: e.target.value })}
                  maxLength={50}
                  className="header-input text-right ml-0 sm:ml-1 w-full sm:w-auto flex-1 sm:flex-none"
                  placeholder="Enter shop code"
                />
              </div>
              <div className="flex items-center justify-end">
                <i className="fas fa-file mr-1"></i>
                PAGE: <span className="text-gray-900 dark:text-slate-100">1 of 1</span>
              </div>
            </div>
          </div>

          {/* Table - desktop/tablet only, see the mobile card view below */}
          <div className="hidden md:block overflow-x-auto mb-4 sm:mb-6 -mx-3 sm:mx-0">
            <div className="inline-block min-w-full align-middle rounded-xl overflow-hidden border border-slate-200 shadow-sm">
              <table className="statement-table w-full border-collapse text-xs sm:text-sm">
                <thead>
                  <tr className="bg-gradient-to-r from-slate-50 to-primary/5 text-slate-700">
                    <th className="row-label border border-gray-300 dark:border-slate-600 p-2 sm:p-3 text-left font-bold bg-gray-100 dark:bg-slate-700 sticky left-0 z-10 min-w-[120px] sm:min-w-[150px]">Category</th>
                    {orderedKeysToDisplay.map(key => {
                      if (key === 'Total') {
                        return (
                          <th key="Total" className="border border-gray-300 dark:border-slate-600 p-2 sm:p-3 text-center bg-gradient-to-br from-primary to-primary-dark text-white font-bold min-w-[90px] sm:min-w-[110px]">
                            <i className="fas fa-calculator mr-1"></i> TOTAL <span className="text-xs opacity-75 block">ಒಟ್ಟು (ಎಲ್ಲಾ)</span>
                          </th>
                        );
                      }
                      return (
                        <th key={key} className="border border-gray-300 dark:border-slate-600 p-2 sm:p-3 text-center min-w-[80px] sm:min-w-[100px]">
                          {key === 'Beer' && <><i className="fas fa-beer mr-1"></i></>}
                          {key === 'Wine' && <><i className="fas fa-wine-glass mr-1"></i></>}
                          {key === 'Brandy' && <><i className="fas fa-wine-bottle mr-1"></i></>}
                          {key === 'Whisky' && <><i className="fas fa-glass-whiskey mr-1"></i></>}
                          {key === 'Rum' && <><i className="fas fa-cocktail mr-1"></i></>}
                          {key === 'GinVodka' && <><i className="fas fa-wine-glass-alt mr-1"></i></>}
                          {key === 'LAB' && <><i className="fas fa-flask mr-1"></i></>}
                          {ALL_CAT_LABELS[key] || key}
                          <span className="text-xs text-gray-500 dark:text-slate-400 block">
                            {key === 'Brandy' && 'ಬ್ರಾಂದಿ'}
                            {key === 'Whisky' && 'ವಿಸ್ಕಿ'}
                            {key === 'Rum' && 'ರಮ್'}
                            {key === 'GinVodka' && 'ಜಿನ್/ವೋಡ್ಕ'}
                            {key === 'LAB' && 'ಲ್ಯಾಬ್'}
                            {key === 'Wine' && 'ವೈನ್'}
                            {key === 'Beer' && 'ಬಿಯರ್'}
                          </span>
                        </th>
                      );
                    })}
                  </tr>
                </thead>
              <tbody>
                {/* Opening Balance Row */}
                <tr className="bg-yellow-50 dark:bg-amber-500/10">
                  <th className="row-label border border-gray-300 dark:border-slate-600 p-2 sm:p-3 text-left font-bold bg-gray-100 dark:bg-slate-700 sticky left-0 z-10">
                    <span className="text-xs sm:text-sm">OPENING BALANCE</span> <span className="text-xs text-gray-500 dark:text-slate-400 block font-normal">ಪ್ರಾರಂಭ ಶಿಲ್ಕು</span>
                  </th>
                  {orderedKeysToDisplay.map(key => {
                    if (key === 'Total') {
                      return (
                        <td key="Total" className="border border-gray-300 dark:border-slate-600 p-2 sm:p-3 text-center bg-primary/10 font-bold text-primary-dark text-xs sm:text-sm">
                          {getTotal('opening').toFixed(3)}
                        </td>
                      );
                    }
                    return (
                      <td key={key} className="border border-gray-300 dark:border-slate-600 p-1.5 sm:p-2">
                        {renderEntryCell('Open', key)}
                      </td>
                    );
                  })}
                </tr>

                {/* Purchase Row */}
                <tr className="bg-yellow-50 dark:bg-amber-500/10">
                  <th className="row-label border border-gray-300 dark:border-slate-600 p-2 sm:p-3 text-left font-bold bg-gray-100 dark:bg-slate-700 sticky left-0 z-10">
                    <span className="text-xs sm:text-sm">PURCHASE</span> <span className="text-xs text-gray-500 dark:text-slate-400 block font-normal">ಖರೀದಿ</span>
                  </th>
                  {orderedKeysToDisplay.map(key => {
                    if (key === 'Total') {
                      return (
                        <td key="Total" className="border border-gray-300 dark:border-slate-600 p-2 sm:p-3 text-center bg-primary/10 font-bold text-primary-dark text-xs sm:text-sm">
                          {getTotal('purchase').toFixed(3)}
                        </td>
                      );
                    }
                    return (
                      <td key={key} className="border border-gray-300 dark:border-slate-600 p-1.5 sm:p-2">
                        {renderEntryCell('Purchase', key)}
                      </td>
                    );
                  })}
                </tr>

                {/* Total Stock Row */}
                <tr className="bg-cyan-50 dark:bg-cyan-500/10">
                  <th className="row-label border border-gray-300 dark:border-slate-600 p-2 sm:p-3 text-left font-bold bg-gray-100 dark:bg-slate-700 sticky left-0 z-10">
                    <span className="text-xs sm:text-sm">TOTAL STOCK</span> <span className="text-xs text-gray-500 dark:text-slate-400 block font-normal">ಒಟ್ಟು ಲಭ್ಯತೆ</span>
                  </th>
                  {orderedKeysToDisplay.map(key => {
                    if (key === 'Total') {
                      return (
                        <td key="Total" className="border border-gray-300 dark:border-slate-600 p-2 sm:p-3 text-center bg-primary/10 font-bold text-primary-dark text-xs sm:text-sm">
                          {(getTotal('opening') + getTotal('purchase')).toFixed(3)}
                        </td>
                      );
                    }
                    return (
                      <td key={key} className="border border-gray-300 dark:border-slate-600 p-2 sm:p-3 text-center bg-gray-100 dark:bg-slate-700 font-bold text-xs sm:text-sm">
                        {getTotalStock(key).toFixed(3)}
                      </td>
                    );
                  })}
                </tr>

                {/* Sales Row */}
                <tr className="bg-pink-50 dark:bg-pink-500/10">
                  <th className="row-label border border-gray-300 dark:border-slate-600 p-2 sm:p-3 text-left font-bold bg-gray-100 dark:bg-slate-700 sticky left-0 z-10">
                    <span className="text-xs sm:text-sm">SALES</span> <span className="text-xs text-gray-500 dark:text-slate-400 block font-normal">ಮಾರಾಟ</span>
                  </th>
                  {orderedKeysToDisplay.map(key => {
                    if (key === 'Total') {
                      const salesTotal = getTotal('opening') + getTotal('purchase') - getTotal('closing');
                      return (
                        <td key="Total" className="border border-gray-300 dark:border-slate-600 p-2 sm:p-3 text-center bg-primary/10 font-bold text-primary-dark text-xs sm:text-sm">
                          {salesTotal.toFixed(3)}
                        </td>
                      );
                    }
                    const sales = getSales(key);
                    return (
                      <td key={key} className="border border-gray-300 dark:border-slate-600 p-2 sm:p-3 text-center font-bold text-xs sm:text-sm bg-gray-100 dark:bg-slate-700">
                        {sales.toFixed(3)}
                      </td>
                    );
                  })}
                </tr>

                {/* Closing Balance Row */}
                <tr className="bg-teal-50 dark:bg-teal-500/10">
                  <th className="row-label border border-gray-300 dark:border-slate-600 p-2 sm:p-3 text-left font-bold bg-gray-100 dark:bg-slate-700 sticky left-0 z-10">
                    <span className="text-xs sm:text-sm">CLOSING BALANCE</span> <span className="text-xs text-gray-500 dark:text-slate-400 block font-normal">ಅಂತಿಮ ಶಿಲ್ಕು</span>
                  </th>
                  {orderedKeysToDisplay.map(key => {
                    if (key === 'Total') {
                      return (
                        <td key="Total" className="border border-gray-300 dark:border-slate-600 p-2 sm:p-3 text-center bg-primary/10 font-bold text-primary-dark text-xs sm:text-sm">
                          {getTotal('closing').toFixed(3)}
                        </td>
                      );
                    }
                    return (
                      <td key={key} className="border border-gray-300 dark:border-slate-600 p-1.5 sm:p-2">
                        {renderEntryCell('Closing', key)}
                      </td>
                    );
                  })}
                </tr>
              </tbody>
            </table>
            </div>
          </div>

          {/* Mobile Card View - one card per category, phone/small-tablet only */}
          {(() => {
            const renderCategoryCard = (category) => {
              const isBeerCat = category === 'Beer';
              const isWineCat = category === 'Wine';
              const totalStock = getTotalStock(category);
              
              let cardStyle = 'border-slate-200 bg-white dark:bg-slate-800';
              let iconClass = 'fa-wine-bottle text-primary';
              if (isBeerCat) {
                cardStyle = 'border-teal-200 bg-teal-50/40 dark:bg-teal-500/10';
                iconClass = 'fa-beer text-teal-600';
              } else if (isWineCat) {
                cardStyle = 'border-purple-200 bg-purple-50/40 dark:bg-purple-500/10';
                iconClass = 'fa-wine-glass text-purple-600';
              }

              return (
                <div key={category} className={`rounded-xl border p-4 space-y-3 ${cardStyle}`}>
                  <h3 className="font-bold text-sm text-slate-700 uppercase tracking-wide flex items-center gap-2">
                    <i className={`fas ${iconClass}`} aria-hidden="true"></i>
                    {ALL_CAT_LABELS[category] || category}
                  </h3>
                  <div>
                    <label className="block text-xs font-semibold text-slate-500 mb-1">Opening Balance</label>
                    {renderEntryCell('Open', category, '-mobile')}
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-slate-500 mb-1">Purchase</label>
                    {renderEntryCell('Purchase', category, '-mobile')}
                  </div>
                  <div className="flex items-center justify-between px-3 py-2.5 rounded-lg bg-primary/10">
                    <span className="text-xs font-semibold text-primary-dark">Total Stock</span>
                    <span className="font-bold text-primary-dark text-sm">{totalStock.toFixed(3)}</span>
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-slate-500 mb-1">Sales</label>
                    <div className="px-3 py-2.5 rounded-lg bg-slate-100">
                      <span className="font-bold text-sm text-slate-800">{getSales(category).toFixed(3)}</span>
                    </div>
                  </div>
                  <div className={`flex items-center justify-between rounded-lg`}>
                    <div className="w-full">
                      <label className="block text-xs font-semibold text-slate-500 mb-1">Closing Balance</label>
                      {renderEntryCell('Closing', category, '-mobile')}
                    </div>
                  </div>
                </div>
              );
            };

            return (
              <div className="md:hidden grid grid-cols-1 sm:grid-cols-2 gap-3 mb-4 sm:mb-6">
                {orderedKeysToDisplay.map(key => {
                  if (key === 'Total') {
                    return (
                      <div key="Total" className="rounded-xl border border-primary/30 bg-gradient-to-br from-primary/10 to-accent/10 p-4 space-y-2 sm:col-span-2">
                        <h3 className="font-bold text-sm text-primary-dark uppercase tracking-wide flex items-center gap-2">
                          <i className="fas fa-calculator" aria-hidden="true"></i> Total
                        </h3>
                        <div className="grid grid-cols-2 gap-2 text-sm">
                          <div className="flex items-center justify-between px-3 py-2 rounded-lg bg-white dark:bg-slate-800/70 shadow-sm border border-slate-100 dark:border-slate-700">
                            <span className="text-xs font-semibold text-slate-500">Opening</span>
                            <span className="font-bold text-slate-800 dark:text-slate-200">{getTotal('opening').toFixed(3)}</span>
                          </div>
                          <div className="flex items-center justify-between px-3 py-2 rounded-lg bg-white dark:bg-slate-800/70 shadow-sm border border-slate-100 dark:border-slate-700">
                            <span className="text-xs font-semibold text-slate-500">Purchase</span>
                            <span className="font-bold text-slate-800 dark:text-slate-200">{getTotal('purchase').toFixed(3)}</span>
                          </div>
                          <div className="flex items-center justify-between px-3 py-2 rounded-lg bg-white dark:bg-slate-800/70 shadow-sm border border-slate-100 dark:border-slate-700">
                            <span className="text-xs font-semibold text-slate-500">Sales</span>
                            <span className="font-bold text-slate-800 dark:text-slate-200">{(getTotal('opening') + getTotal('purchase') - getTotal('closing')).toFixed(3)}</span>
                          </div>
                          <div className="flex items-center justify-between px-3 py-2 rounded-lg bg-white dark:bg-slate-800/70 shadow-sm border border-slate-100 dark:border-slate-700">
                            <span className="text-xs font-semibold text-slate-500">Closing</span>
                            <span className="font-bold text-slate-800 dark:text-slate-200">
                              {getTotal('closing').toFixed(3)}
                            </span>
                          </div>
                        </div>
                      </div>
                    );
                  }
                  return renderCategoryCard(key);
                })}
              </div>
            );
          })()}

          {/* Permit Invoice Details Section */}
          {hasPermission('liter_statement', 'permit_invoice_details') && (
            <div className="mt-8 pt-6 border-t border-gray-200 dark:border-slate-700">
              <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3 mb-4">
                <h2 className="text-base sm:text-lg font-bold text-gray-800 dark:text-slate-100 flex items-center gap-2">
                  <i className="fas fa-file-invoice text-primary dark:text-accent"></i>
                  Permit Invoice Details
                  {invoices.length > 0 && (
                    <span className="text-xs font-normal text-gray-400 dark:text-slate-500">({invoices.length}/{MAX_INVOICE_ROWS})</span>
                  )}
                </h2>
                <Button
                  type="button"
                  variant="primary"
                  size="sm"
                  icon="fa-plus"
                  onClick={handleAddInvoiceRow}
                  disabled={invoices.length >= MAX_INVOICE_ROWS}
                >
                  Add Row
                </Button>
              </div>

              <div className="overflow-x-auto rounded-xl border border-gray-200 dark:border-slate-700 shadow-sm max-h-[400px]">
                <table className="w-full border-collapse text-xs sm:text-sm text-left">
                  <thead className="bg-gray-50 dark:bg-slate-700 sticky top-0 z-10 border-b border-gray-200 dark:border-slate-600">
                    <tr className="text-gray-700 dark:text-slate-200 font-bold">
                      <th className="p-3 text-center w-12">S.No</th>
                      <th className="p-3 min-w-[120px]">Date</th>
                      <th className="p-3 min-w-[120px]">Invoice No</th>
                      <th className="p-3 text-right w-24">IML</th>
                      <th className="p-3 text-right w-24">BEER</th>
                      <th className="p-3 text-right w-24">Wine</th>
                      <th className="p-3 text-right w-24">LAB</th>
                      <th className="p-3 text-right w-28 bg-primary/5 text-primary dark:text-accent">Total</th>
                      <th className="p-3 text-center w-16 pdf-hide">Del</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100 dark:divide-slate-700/60 bg-white dark:bg-slate-800">
                    {invoices.length === 0 ? (
                      <tr>
                        <td colSpan="9" className="p-8 text-center text-gray-400 dark:text-slate-500 font-medium">
                          No invoice records added. Click "Add Row" to enter records.
                        </td>
                      </tr>
                    ) : (
                      invoices.map((inv, idx) => (
                        <tr key={inv.id} className="hover:bg-slate-50/50 dark:hover:bg-slate-700/20 transition-colors">
                          <td className="p-3 text-center font-medium text-gray-500 dark:text-slate-400">{idx + 1}</td>
                          <td className="p-2">
                            <input
                              type="date"
                              value={inv.date || ''}
                              onChange={(e) => handleInvoiceFieldChange(inv.id, 'date', e.target.value)}
                              className="w-full p-1.5 border rounded border-gray-300 dark:border-slate-600 dark:bg-slate-700 dark:text-white focus:ring-1 focus:ring-primary text-xs"
                            />
                          </td>
                          <td className="p-2">
                            <input
                              type="text"
                              value={inv.invoice_no || ''}
                              onChange={(e) => handleInvoiceFieldChange(inv.id, 'invoice_no', e.target.value)}
                              className="w-full p-1.5 border rounded border-gray-300 dark:border-slate-600 dark:bg-slate-700 dark:text-white focus:ring-1 focus:ring-primary text-xs font-semibold"
                            />
                          </td>
                          <td className="p-2">
                            <input
                              type="number"
                              step="0.001"
                              min="0"
                              value={inv.iml || ''}
                              onChange={(e) => handleInvoiceFieldChange(inv.id, 'iml', e.target.value)}
                              onInput={(e) => { if (e.target.value.length > 7) e.target.value = e.target.value.slice(0, 7); }}
                              onWheel={(e) => e.target.blur()}
                              onKeyDown={(e) => ['ArrowUp', 'ArrowDown'].includes(e.key) && e.preventDefault()}
                              className="w-full p-1.5 border rounded border-gray-300 dark:border-slate-600 dark:bg-slate-700 dark:text-white focus:ring-1 focus:ring-primary text-xs text-right font-medium"
                              placeholder="0"
                            />
                          </td>
                          <td className="p-2">
                            <input
                              type="number"
                              step="0.001"
                              min="0"
                              value={inv.beer || ''}
                              onChange={(e) => handleInvoiceFieldChange(inv.id, 'beer', e.target.value)}
                              onInput={(e) => { if (e.target.value.length > 7) e.target.value = e.target.value.slice(0, 7); }}
                              onWheel={(e) => e.target.blur()}
                              onKeyDown={(e) => ['ArrowUp', 'ArrowDown'].includes(e.key) && e.preventDefault()}
                              className="w-full p-1.5 border rounded border-gray-300 dark:border-slate-600 dark:bg-slate-700 dark:text-white focus:ring-1 focus:ring-primary text-xs text-right font-medium"
                              placeholder="0"
                            />
                          </td>
                          <td className="p-2">
                            <input
                              type="number"
                              step="0.001"
                              min="0"
                              value={inv.wine || ''}
                              onChange={(e) => handleInvoiceFieldChange(inv.id, 'wine', e.target.value)}
                              onInput={(e) => { if (e.target.value.length > 7) e.target.value = e.target.value.slice(0, 7); }}
                              onWheel={(e) => e.target.blur()}
                              onKeyDown={(e) => ['ArrowUp', 'ArrowDown'].includes(e.key) && e.preventDefault()}
                              className="w-full p-1.5 border rounded border-gray-300 dark:border-slate-600 dark:bg-slate-700 dark:text-white focus:ring-1 focus:ring-primary text-xs text-right font-medium"
                              placeholder="0"
                            />
                          </td>
                          <td className="p-2">
                            <input
                              type="number"
                              step="0.001"
                              min="0"
                              value={inv.lab || ''}
                              onChange={(e) => handleInvoiceFieldChange(inv.id, 'lab', e.target.value)}
                              onInput={(e) => { if (e.target.value.length > 7) e.target.value = e.target.value.slice(0, 7); }}
                              onWheel={(e) => e.target.blur()}
                              onKeyDown={(e) => ['ArrowUp', 'ArrowDown'].includes(e.key) && e.preventDefault()}
                              className="w-full p-1.5 border rounded border-gray-300 dark:border-slate-600 dark:bg-slate-700 dark:text-white focus:ring-1 focus:ring-primary text-xs text-right font-medium"
                              placeholder="0"
                            />
                          </td>
                          <td className="p-3 text-right font-bold text-primary dark:text-accent bg-primary/5">
                            {parseFloat(inv.total || 0).toFixed(3)}
                          </td>
                          <td className="p-2 text-center pdf-hide">
                            <button
                              type="button"
                              onClick={() => handleDeleteInvoiceRow(inv.id)}
                              className="p-1.5 bg-red-500 hover:bg-red-600 text-white rounded transition-colors btn-hover-scale"
                              title="Delete Row"
                            >
                              <i className="fas fa-trash-alt text-xs"></i>
                            </button>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                  {invoices.length > 0 && (
                    <tfoot className="bg-slate-50 dark:bg-slate-700/80 border-t-2 border-gray-200 dark:border-slate-600 font-bold">
                      <tr>
                        <td colSpan="3" className="p-3 text-right text-gray-700 dark:text-slate-200">Summary Total:</td>
                        <td className="p-3 text-right text-gray-800 dark:text-slate-100">
                          {getInvoiceTotals().iml.toFixed(3)}
                        </td>
                        <td className="p-3 text-right text-gray-800 dark:text-slate-100">
                          {getInvoiceTotals().beer.toFixed(3)}
                        </td>
                        <td className="p-3 text-right text-gray-800 dark:text-slate-100">
                          {getInvoiceTotals().wine.toFixed(3)}
                        </td>
                        <td className="p-3 text-right text-gray-800 dark:text-slate-100">
                          {getInvoiceTotals().lab.toFixed(3)}
                        </td>
                        <td className="p-3 text-right text-primary dark:text-accent bg-primary/10">
                          {getInvoiceTotals().total.toFixed(3)}
                        </td>
                        <td className="p-3 pdf-hide"></td>
                      </tr>
                    </tfoot>
                  )}
                </table>
              </div>
            </div>
          )}

        </div>
      </div>

      {/* Calculation Modal */}
      {modalOpen && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-2 sm:p-4 pt-[max(0.5rem,env(safe-area-inset-top))] pb-[max(0.5rem,env(safe-area-inset-bottom))]"
          onClick={closeModal}
          role="dialog"
          aria-modal="true"
          aria-labelledby="calc-modal-title"
        >
          <div className="bg-white dark:bg-slate-800 rounded-xl shadow-2xl max-w-4xl w-full max-h-dvh-modal flex flex-col m-2 sm:m-0" onClick={(e) => e.stopPropagation()}>
            <div className="bg-gradient-to-r from-primary to-primary-dark text-white p-3 sm:p-4 rounded-t-xl">
              <h2 id="calc-modal-title" className="text-base sm:text-lg font-bold flex items-center">
                <i className="fas fa-calculator mr-2" aria-hidden="true"></i> Calculate {modalData.row} {modalData.brand}
              </h2>
            </div>
            <div className="flex-1 overflow-auto p-3 sm:p-4">
              <div className="overflow-x-auto">
                <table className="calc-modal-table w-full border-collapse border border-gray-300 dark:border-slate-600 text-xs sm:text-sm">
                  <thead className="bg-gray-100 dark:bg-slate-700 sticky top-0">
                    <tr>
                      <th className="border border-gray-300 dark:border-slate-600 p-2 text-left w-16">SL NO</th>
                      <th className="border border-gray-300 dark:border-slate-600 p-2 text-left">Total Bottles</th>
                      <th className="border border-gray-300 dark:border-slate-600 p-2 text-left w-32">ML</th>
                      <th className="border border-gray-300 dark:border-slate-600 p-2 text-right w-24">Liter</th>
                      <th className="border border-gray-300 dark:border-slate-600 p-2 text-center w-16">Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {calculationRows.map((row, index) => (
                      <tr key={index} className="border-b">
                        <td className="p-2 text-center font-medium">{index + 1}</td>
                        <td className="p-2">
                          <input
                            type="number"
                            className="w-full p-2 sm:p-1 border rounded text-sm min-h-[44px] sm:min-h-[auto]"
                            min="0"
                            value={row.bottles}
                            onChange={(e) => updateCalculationRow(index, 'bottles', e.target.value)}
                            onWheel={(e) => e.target.blur()}
                            onKeyDown={(e) => ['ArrowUp', 'ArrowDown'].includes(e.key) && e.preventDefault()}
                            placeholder="0"
                          />
                        </td>
                        <td className="p-2">
                          <input
                            type="number"
                            className="w-full p-2 sm:p-1 border rounded text-sm min-h-[44px] sm:min-h-[auto]"
                            min="0"
                            value={row.ml}
                            onChange={(e) => updateCalculationRow(index, 'ml', e.target.value)}
                            onWheel={(e) => e.target.blur()}
                            onKeyDown={(e) => ['ArrowUp', 'ArrowDown'].includes(e.key) && e.preventDefault()}
                            placeholder="750"
                          />
                        </td>
                        <td className="p-2 text-right font-medium">{row.liter.toFixed(3)}</td>
                        <td className="p-2 text-center">
                          <button
                            type="button"
                            onClick={() => removeCalculationRow(index)}
                            className="text-red-500 hover:text-red-700 p-1 transition-colors"
                            title="Remove Row"
                          >
                            <i className="fas fa-trash-alt"></i>
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <Button variant="secondary" size="sm" className="mt-3 sm:mt-2 w-full sm:w-auto" icon="fa-plus" onClick={addCalculationRow}>
                Add Row
              </Button>
              <div className="mt-4 p-3 sm:p-2 bg-blue-50 border rounded text-right font-bold flex flex-col sm:flex-row justify-between items-center gap-2 sm:gap-0">
                <span className="text-blue-700 flex items-center justify-center sm:justify-start">
                  <i className="fas fa-calculator mr-1"></i> Total Liter:
                </span>
                <span className="text-blue-700 text-base sm:text-lg">
                  {calculationRows.reduce((sum, row) => sum + (parseFloat(row.liter) || 0), 0).toFixed(3)}
                </span>
              </div>
            </div>
            <div className="p-3 sm:p-4 border-t bg-gray-50 dark:bg-slate-700/50 flex flex-col sm:flex-row justify-end gap-2 rounded-b-xl">
              <Button variant="success" fullWidth className="sm:w-auto" icon="fa-check" onClick={applyCalculation}>
                OK
              </Button>
              <Button variant="secondary" fullWidth className="sm:w-auto" icon="fa-times" onClick={closeModal}>
                Cancel
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default LiterStatementPage;
